import { useEffect, useRef, useState } from 'react';

import { useUser } from '@clerk/react';
import Intercom from '@intercom/messenger-js-sdk';

import { APP_PAGES } from '../routes/constants';

const INTERCOM_APP_ID = 'mctb0r8j';
const CONSENT_EVENT = 'cookie_consent_update';
const FUNCTIONALITY_STORAGE_GRANTED = 'granted';

const checkConsentGranted = (): boolean => {
  const dataLayer = window.dataLayer;
  if (!Array.isArray(dataLayer)) {
    return false;
  }

  for (let i = dataLayer.length - 1; i >= 0; i--) {
    const entry = dataLayer[i];
    if (
      entry?.[0] === 'consent' &&
      entry?.[1] === 'update' &&
      entry?.[2]?.functionality_storage === FUNCTIONALITY_STORAGE_GRANTED
    ) {
      return true;
    }
  }

  return false;
};

export const useIntercomInitialization = () => {
  const { user, isLoaded } = useUser();
  const hasInitialized = useRef(false);
  const [consentGranted, setConsentGranted] = useState(false);

  useEffect(() => {
    if (consentGranted) {
      return;
    }

    const handleConsent = () => {
      if (checkConsentGranted()) {
        setConsentGranted(true);
      }
    };

    handleConsent();

    window.addEventListener(CONSENT_EVENT, handleConsent);
    return () => window.removeEventListener(CONSENT_EVENT, handleConsent);
  }, [consentGranted]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (!consentGranted) {
      return;
    }

    if (!window.__TRACKING_ENABLED__) {
      return;
    }

    if (hasInitialized.current) {
      return;
    }

    if (user) {
      const createdAtTimestamp =
        user.createdAt && typeof user.createdAt === 'number'
          ? Math.floor(user.createdAt / 1000)
          : undefined;

      const isOnSupportRoute = window.location.pathname.startsWith(
        APP_PAGES.SUPPORT,
      );

      Intercom({
        app_id: INTERCOM_APP_ID,
        user_id: user.id,
        name: user.fullName || undefined,
        email: user.emailAddresses[0]?.emailAddress,
        created_at: createdAtTimestamp,
        hide_default_launcher: !isOnSupportRoute,
      });

      hasInitialized.current = true;
    }
  }, [isLoaded, user, consentGranted]);
};
